/**
 * FxPnlCard — monthly FX gain/loss card for the dashboard.
 *
 * Shows the user how much the rate moved between the moment they
 * recorded each FX movement and TODAY. Surfaces:
 *   • Net delta for the month (the headline)
 *   • Income vs expense split (FX-tagged movements only)
 *   • Per-currency breakdown (USD: -X COP, EUR: +Y COP, …)
 *   • Top-3 biggest swings (clickable → /history)
 *
 * Hidden entirely when the user has zero FX movements for the period —
 * a freelancer who bills only in their profile currency shouldn't see
 * a confusing empty card.
 */
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { formatMoney } from "../../lib/api";
import { useFxPnl, useProfile } from "../../hooks/queries";
import { useLang } from "../../contexts/LangContext";
import { ArrowLeftRight, TrendingDown, TrendingUp, ChevronDown, ChevronUp } from "lucide-react";

const monthBounds = (year, monthIdx) => {
  const start = new Date(Date.UTC(year, monthIdx, 1, 0, 0, 0));
  const end = new Date(Date.UTC(year, monthIdx + 1, 1, 0, 0, 0));
  return { start: start.toISOString(), end: end.toISOString() };
};

export function FxPnlCard() {
  const { lang } = useLang();
  const { data: profile } = useProfile();
  const ccy = profile?.currency || "USD";
  const now = useMemo(() => new Date(), []);
  const { start, end } = useMemo(
    () => monthBounds(now.getUTCFullYear(), now.getUTCMonth()),
    [now],
  );
  const { data, isLoading } = useFxPnl(start, end);
  const [expanded, setExpanded] = useState(false);

  if (isLoading || !data) return null;
  const count = data.totals?.movements_count || 0;
  // No FX movements this month → hide the card entirely. Showing an empty
  // state would be noisy for ARG / COP / single-currency freelancers.
  if (!count) return null;

  const isEs = lang === "es";
  const net = data.totals.net_delta || 0;
  const isGain = net >= 0;
  const monthLabel = now.toLocaleDateString(isEs ? "es" : "en", {
    month: "long",
    year: "numeric",
  });

  return (
    <Card
      data-testid="fx-pnl-card"
      className="p-5 rounded-2xl border-slate-200 bg-gradient-to-br from-white to-slate-50"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-2xl bg-sky-50 text-sky-700 flex items-center justify-center">
            <ArrowLeftRight className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-sky-50 text-sky-700 border border-sky-200">
                {isEs ? "FX" : "FX"}
              </span>
              <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 capitalize">
                {monthLabel}
              </span>
            </div>
            <h3
              data-testid="fx-pnl-title"
              className="font-display text-lg font-bold text-slate-900"
            >
              {isEs ? "Ganancia / pérdida por FX" : "FX gain / loss"}
            </h3>
            <p className="text-xs text-slate-500 max-w-md mt-0.5">
              {isEs
                ? `Compara la tasa registrada en cada movimiento contra la tasa de mercado de hoy en ${count} ${count === 1 ? "movimiento" : "movimientos"} de moneda extranjera.`
                : `Compares each movement's locked rate against today's market rate across ${count} foreign-currency ${count === 1 ? "movement" : "movements"}.`}
            </p>
          </div>
        </div>
        <div className="text-right">
          <div
            data-testid="fx-pnl-net"
            className={`font-display text-2xl sm:text-3xl font-bold tabular-nums flex items-center gap-2 ${isGain ? "text-emerald-700" : "text-red-600"}`}
          >
            {isGain ? <TrendingUp className="h-5 w-5" /> : <TrendingDown className="h-5 w-5" />}
            {isGain ? "+" : "-"}
            {formatMoney(Math.abs(net), ccy)}
          </div>
          <div className="text-[10px] text-slate-400 mt-1">
            {isEs ? "neto del mes" : "net for the month"}
          </div>
        </div>
      </div>

      {/* Income vs expense split — kept compact, only shown when both sides have movement */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div
          data-testid="fx-pnl-income-delta"
          className="rounded-xl bg-white border border-slate-200 p-3"
        >
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
            {isEs ? "En ingresos FX" : "On FX income"}
          </div>
          <div
            className={`font-display text-lg font-bold tabular-nums mt-0.5 ${data.totals.income_delta >= 0 ? "text-emerald-700" : "text-red-600"}`}
          >
            {data.totals.income_delta >= 0 ? "+" : "-"}
            {formatMoney(Math.abs(data.totals.income_delta), ccy)}
          </div>
        </div>
        <div
          data-testid="fx-pnl-expense-delta"
          className="rounded-xl bg-white border border-slate-200 p-3"
        >
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
            {isEs ? "En gastos FX" : "On FX expense"}
          </div>
          <div
            className={`font-display text-lg font-bold tabular-nums mt-0.5 ${data.totals.expense_delta >= 0 ? "text-emerald-700" : "text-red-600"}`}
          >
            {data.totals.expense_delta >= 0 ? "+" : "-"}
            {formatMoney(Math.abs(data.totals.expense_delta), ccy)}
          </div>
        </div>
      </div>

      {/* Per-currency breakdown chips (always shown when ≥1 currency) */}
      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="fx-pnl-by-currency">
        {data.by_currency.map((b) => {
          const pos = b.net_delta >= 0;
          return (
            <span
              key={b.currency}
              data-testid={`fx-pnl-ccy-${b.currency}`}
              className={`inline-flex items-center gap-1.5 text-[11px] font-bold tabular-nums rounded-full px-2.5 py-1 ring-1 ${
                pos
                  ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                  : "bg-red-50 text-red-600 ring-red-200"
              }`}
            >
              <span className="uppercase tracking-wider">{b.currency}</span>
              <span>
                {pos ? "+" : "-"}
                {formatMoney(Math.abs(b.net_delta), ccy)}
              </span>
              <span className="text-slate-400 font-normal">· {b.count}</span>
            </span>
          );
        })}
      </div>

      {/* Expandable "biggest swings" list */}
      {data.items.length > 0 && (
        <>
          <Button
            data-testid="fx-pnl-toggle"
            type="button"
            variant="ghost"
            size="sm"
            className="text-xs text-slate-600 hover:text-slate-900 gap-1 h-7 px-2"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {isEs ? "Mayores movimientos" : "Biggest swings"}
          </Button>
          {expanded && (
            <div className="mt-2 grid gap-1.5" data-testid="fx-pnl-items">
              {data.items.slice(0, 5).map((it) => {
                const pos = it.delta >= 0;
                return (
                  <Link
                    key={it.id}
                    to="/history"
                    data-testid={`fx-pnl-item-${it.id}`}
                    className="flex items-center justify-between gap-3 rounded-lg bg-white border border-slate-200 px-2.5 py-1.5 hover:border-slate-300 transition-colors text-xs"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 truncate" title={it.description}>
                        {it.description}
                      </div>
                      <div className="text-[10px] text-slate-500 tabular-nums">
                        {Number(it.original_amount).toLocaleString(isEs ? "es" : "en", { maximumFractionDigits: 2 })}{" "}
                        {it.original_currency} · {it.locked_rate?.toLocaleString(isEs ? "es" : "en", { maximumFractionDigits: 4 })} → {it.today_rate?.toLocaleString(isEs ? "es" : "en", { maximumFractionDigits: 4 })}
                      </div>
                    </div>
                    <span
                      className={`tabular-nums font-bold whitespace-nowrap ${pos ? "text-emerald-700" : "text-red-600"}`}
                    >
                      {pos ? "+" : "-"}
                      {formatMoney(Math.abs(it.delta), ccy)}
                    </span>
                  </Link>
                );
              })}
            </div>
          )}
        </>
      )}

      {data.fx_rates_fetched_at && (
        <div className="text-[10px] text-slate-400 mt-2.5">
          {isEs ? "Tasa de comparación actualizada el " : "Comparison rate as of "}
          {new Date(data.fx_rates_fetched_at).toLocaleString(isEs ? "es" : "en")}
        </div>
      )}
    </Card>
  );
}
